/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { useNavigateWithParams } from '../../hooks/useNavigateWithParams'
import { getFileDownloadUrl } from '../../services/api/files'
import { toast } from '../../services/toast'
import TaskDetailsModal from './Actions/details'
import { TaskCompleteModal } from './Actions/complete'
import { TaskCancel } from './Actions'
import { Task, TaskType, WorkflowStepType, WorkflowType, FsiType } from './types'
import { AclLevel } from '../../constants/fsi'
import ActionsMenu from '../../components/shared/ActionsMenu'
import { TASK_ACTIONS } from '../../constants/workflow'
import WorkflowCommentsModal from '../../components/modals/WorkflowCommentsModal'
import { WorkflowEditModal } from '../../components/modals/WorkflowEditModal'
import { WorkflowReviewModal } from '../../components/modals/WorkflowReviewModal'
import { WorkflowQcApproversModal } from '../../components/modals/WorkflowQcApproversModal'
import { WorkflowApproversModal } from '../../components/modals/WorkflowApproversModal'
import { WorkflowTraineesModal } from '../../components/modals/WorkflowTraineesModal'
import { WorkflowRejectionReviewersModal } from '../../components/modals/WorkflowRejectionReviewersModal'
import { WorkflowCollaboratorsModal } from '../../components/modals/WorkflowCollaboratorsModal'
import { VersionsModal } from '../../components/modals/VersionsModal'
import { TaskManageDocsModal } from './Actions/manage-docs'
import FsiPickerModal from '../../components/modals/FsiPickerModal'
import { UploadFileModal } from '../../components/modals/UploadFileModal'
import { FileDiffModal } from '../../components/modals/FileDiffModal'
import FsiPropertiesModal from '../../pages/browser/properties/FsiPropertiesModal'
import UploadFileRequestModal from '~/pages/browser/upload-file/UploadFileRequestModal'

interface TaskMenuProps {
  task: Task
  hideDetails?: boolean
  onClose?: () => void
}

interface MenuItemData {
  action?: () => void
  label?: string
  disabled?: boolean
  value?: string
  title?: string
  submenu?: MenuItemData[]
  divider?: boolean
}

/**
 * Task Actions Menu Component
 *
 * Displays context menu with available actions for a task
 * Based on lego/page/tasks/TableMenu.jsx
 */
const TaskMenu: FC<TaskMenuProps> = ({ task, hideDetails = false, onClose }) => {
  const { t } = useTranslation()
  const { api } = useServiceContext()
  const { showModal } = useModal()
  const navigate = useNavigateWithParams()
  const [isOpen, setIsOpen] = useState(false)
  const [optionsLoaded, setOptionsLoaded] = useState(false)
  const [isFsiPickerOpen, setIsFsiPickerOpen] = useState(false)
  const [currentTaskForUpload, setCurrentTaskForUpload] = useState<Task | null>(null)
  const [items, setItems] = useState<MenuItemData[]>([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])

  const getCompleteTaskOptions = (taskData: Task): MenuItemData[] => {
    const menu: MenuItemData[] = []

    // USER_REJECTED_TASK type
    if (taskData.taskType === TaskType.USER_REJECTED_TASK) {
      if (taskData.flags?.canRevert) {
        menu.push({
          value: 'ACTION_COMMAND_REVERT_CR_TO_EDIT_REVIEW',
          action: () => {
            // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
            const file = taskData.files?.[0]
              ? taskData.files[0].draftFsi
                ? taskData.files[0].draftFsi
                : taskData.files[0].targetFsi
                  ? taskData.files[0].targetFsi
                  : taskData.files[0].sourceFsi
              : null

            // Navigate to show-file if there's a viewable file
            if (file && file.type !== FsiType.PLACEHOLDER) {
              navigate(
                `/show-file/${file.id}?action=taskComplete&taskId=${taskData.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
              )
            } else {
              // Show modal if no file or placeholder
              showModal(<TaskCompleteModal task={taskData} type={TASK_ACTIONS.taskComplete} />)
            }
          }
        })
      }

      if (taskData.flags?.canCancel) {
        menu.push({
          value: 'ACTION_COMMAND_CANCEL_CR',
          action: () => {
            showModal(
              <TaskCancel
                task={taskData}
                onFinished={() => {
                  // Task cancelled successfully
                }}
              />
            )
          }
        })
      }

      if (taskData.flags?.canReassign) {
        menu.push({
          value: 'TASKS_TASK_ACTIONS_REASSIGN_TASK',
          action: () => {
            showModal(<TaskCompleteModal task={taskData} type={TASK_ACTIONS.taskReassign} />)
          }
        })
      }
    }
    // USER task type
    else if (taskData.taskType === TaskType.USER) {
      if (taskData.flags?.canApprove) {
        menu.push({
          value: 'TASKS_TASK_ACTIONS_APPROVE_TASK',
          action: () => {
            // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
            const file = taskData.files?.[0]
              ? taskData.files[0].draftFsi
                ? taskData.files[0].draftFsi
                : taskData.files[0].targetFsi
                  ? taskData.files[0].targetFsi
                  : taskData.files[0].sourceFsi
              : null

            // For QC_REVIEW workflows, we need to get the first file from workflow files
            if (taskData.workflowType === WorkflowType.QC_REVIEW) {
              // Load workflow files and navigate to first file with action
              api.workflows
                .getWorkflowFiles(Number(taskData.workflowId), { pageStart: 0, pageSize: 1 })
                .then((resp) => {
                  const firstFile = resp?.elements?.[0]?.draftFsi
                  if (firstFile) {
                    // For QC workflows, we'd navigate to showFileQC route
                    // For now, use regular show-file route
                    navigate(
                      `/show-file-qc/${firstFile.id}?action=taskComplete&taskId=${taskData.id}&versionId=${(firstFile as any).versionId || -1}&minorVersionId=${(firstFile as any).minorVersionId || 0}`
                    )
                  }
                })
                .catch(() => {
                  // Fallback to modal if API fails
                  showModal(<TaskCompleteModal task={taskData} type={TASK_ACTIONS.taskComplete} />)
                })
            } else if (
              file &&
              file.type !== FsiType.PLACEHOLDER &&
              taskData.workflowStepType !== WorkflowStepType.DOWNLOAD_EOS
            ) {
              // Navigate to show-file for regular workflows with viewable files
              navigate(
                `/show-file/${file.id}?action=taskComplete&taskId=${taskData.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
              )
            } else {
              // Show modal if no file, placeholder, or DOWNLOAD_EOS step
              showModal(<TaskCompleteModal task={taskData} type={TASK_ACTIONS.taskComplete} />)
            }
          }
        })
      }

      if (taskData.flags?.canCancelRATask) {
        menu.push({
          value: 'ACTION_COMMAND_CANCEL_TASK',
          action: () => {
            showModal(
              <TaskCancel
                task={taskData}
                isRATask={true}
                onFinished={() => {
                  // RA Task cancelled successfully
                }}
              />
            )
          }
        })
      }

      // Not TRAINING type tasks
      if (taskData.workflowStepType !== WorkflowStepType.TRAINING) {
        if (taskData.flags?.canReject) {
          menu.push({
            value: 'TASKS_TASK_ACTIONS_REJECT_TASK',
            action: () => {
              // Get the file to view (prioritize draftFsi, then targetFsi, then sourceFsi)
              const file = taskData.files?.[0]
                ? taskData.files[0].draftFsi
                  ? taskData.files[0].draftFsi
                  : taskData.files[0].targetFsi
                    ? taskData.files[0].targetFsi
                    : taskData.files[0].sourceFsi
                : null

              // Navigate to show-file if there's a viewable file
              if (file && file.type !== FsiType.PLACEHOLDER) {
                navigate(
                  `/show-file/${file.id}?action=taskReject&taskId=${taskData.id}&versionId=${file.versionId || -1}&minorVersionId=${file.minorVersionId || 0}`
                )
              } else {
                // Show modal if no file or placeholder
                showModal(<TaskCompleteModal task={taskData} type={TASK_ACTIONS.taskReject} />)
              }
            }
          })
        }

        if (taskData.flags?.canReassign) {
          menu.push({
            value: 'TASKS_TASK_ACTIONS_REASSIGN_TASK',
            action: () => {
              showModal(<TaskCompleteModal task={taskData} type={TASK_ACTIONS.taskReassign} />)
            }
          })
        }
      }
    }

    return menu
  }

  const buildMenuOptions = (taskData: Task, workflowData?: any): MenuItemData[] => {
    const taskOptions = getCompleteTaskOptions(taskData)
    const menu: MenuItemData[] = [...taskOptions]
    const lowerMenu: MenuItemData[] = []

    // USER_REJECTED_TASK specific options
    if (taskData.taskType === TaskType.USER_REJECTED_TASK) {
      // Add divider if there are task options
      if (taskOptions.length > 0) {
        menu.push({ divider: true })
      }

      // View Task Details
      if (!hideDetails) {
        menu.push({
          value: 'BROWSER_ACTION_VIEW_TASK_DETAILS',
          action: () => {
            showModal(<TaskDetailsModal taskId={taskData.id} onClose={() => {}} />)
          }
        })
      }

      // View Comments submenu
      if (taskData.flags?.canViewComments && workflowData?.id) {
        menu.push({
          value: 'ACTION_COMMAND_VIEW',
          submenu: [
            {
              value: 'VIEW_COMMENTS_ACTION',
              action: () => {
                showModal(<WorkflowCommentsModal workflow={workflowData} />)
              }
            }
          ]
        })
      }

      // Versions action
      if (!taskData.flags?.multifileWorkflow && taskData.files?.[0]) {
        // Determine which file to use based on type and ACL level
        const file = (() => {
          if (
            taskData.files[0].draftFsi &&
            taskData.files[0].draftFsi.type !== FsiType.PLACEHOLDER &&
            !([AclLevel.NONE, AclLevel.BROWSE] as string[]).includes((taskData.files[0].draftFsi as any).aclLevel)
          ) {
            return taskData.files[0].draftFsi
          } else if (
            taskData.files[0].sourceFsi &&
            taskData.files[0].sourceFsi.type !== FsiType.PLACEHOLDER &&
            !([AclLevel.NONE, AclLevel.BROWSE] as string[]).includes((taskData.files[0].sourceFsi as any).aclLevel)
          ) {
            return taskData.files[0].sourceFsi
          }
          return null
        })()

        if (file) {
          menu.push({
            value: 'BROWSER_ACTION_VERSIONS',
            action: () => {
              showModal(<VersionsModal fsiId={String(file.id)} />)
            }
          })
        }
      }
    }
    // USER task type specific options
    else if (taskData.taskType === TaskType.USER) {
      if (taskData.workflowStepType !== WorkflowStepType.TRAINING) {
        // View Task Details
        if (!hideDetails && taskData.workflowStepType !== WorkflowStepType.DOWNLOAD_EOS) {
          menu.push({
            value: 'BROWSER_ACTION_VIEW_TASK_DETAILS',
            action: () => {
              showModal(<TaskDetailsModal taskId={taskData.id} onClose={() => {}} />)
            }
          })
        } else if (!hideDetails && taskData.workflowStepType === WorkflowStepType.DOWNLOAD_EOS) {
          lowerMenu.push({
            value: 'BROWSER_ACTION_VIEW_TASK_DETAILS',
            action: () => {
              showModal(<TaskDetailsModal taskId={taskData.id} onClose={() => {}} />)
            }
          })
        }

        // View submenu
        const subViewMenu: MenuItemData[] = []

        // View Current Document
        if (
          taskData.workflowType !== WorkflowType.IMPORT &&
          taskData.workflowType !== WorkflowType.QC_REVIEW &&
          taskData.files?.length === 1 &&
          taskData.files[0].sourceFsi &&
          taskData.files[0].sourceFsi.id &&
          taskData.files[0].sourceFsi.type !== FsiType.PLACEHOLDER
        ) {
          subViewMenu.push({
            value: 'ACTION_COMMAND_VIEW_DOCUMENT_CURRENT',
            action: async () => {
              const fileId = taskData.files?.[0]?.sourceFsi?.id
              if (!fileId) return

              try {
                // Fetch file details to verify access before navigation
                const fileDetails = await api.files.getDetails(Number(fileId))

                // Check ACL level
                if (fileDetails.acl === 'NONE' || fileDetails.acl === 'BROWSE') {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                  return
                }

                // Check if file is valid
                if (!fileDetails.valid) {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_VALID_MSG'))
                  return
                }

                // Check if file is a placeholder (extra safety check)
                if (fileDetails.type === 'PLACEHOLDER') {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_PLACEHOLDER_CANNOT_OPEN'))
                  return
                }

                // User has access - navigate to file viewer
                navigate(`/show-file/${fileId}`)
              } catch (error: any) {
                console.error('Error fetching file details:', error)
                if (error?.status === 403 || error?.status === 401) {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                } else {
                  toast.error(t('ERROR_IN_GENERATING_PROCESS'))
                }
              }
            }
          })
        }

        // View Draft Document
        if (
          taskData.workflowType !== WorkflowType.IMPORT &&
          taskData.workflowType !== WorkflowType.QC_REVIEW &&
          taskData.workflowType !== WorkflowType.EOS_DOWNLOAD_AND_ACKNOWLEDGE &&
          taskData.files?.length === 1 &&
          taskData.files[0].draftFsi &&
          taskData.files[0].draftFsi.id &&
          taskData.files[0].draftFsi.type !== FsiType.PLACEHOLDER
        ) {
          subViewMenu.push({
            value: 'ACTION_COMMAND_VIEW_DOCUMENT_DRAFT',
            action: async () => {
              const fileId = taskData.files?.[0]?.draftFsi?.id
              if (!fileId) return

              try {
                // Fetch file details to verify access before navigation
                const fileDetails = await api.files.getDetails(Number(fileId))

                // Check ACL level
                if (fileDetails.acl === 'NONE' || fileDetails.acl === 'BROWSE') {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                  return
                }

                // Check if file is valid
                if (!fileDetails.valid) {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_VALID_MSG'))
                  return
                }

                // Check if file is a placeholder (extra safety check)
                if (fileDetails.type === 'PLACEHOLDER') {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_PLACEHOLDER_CANNOT_OPEN'))
                  return
                }

                // User has access - navigate to file viewer
                navigate(`/show-file/${fileId}`)
                onClose?.()
              } catch (error: any) {
                console.error('Error fetching file details:', error)
                if (error?.status === 403 || error?.status === 401) {
                  toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                } else {
                  toast.error(t('ERROR_IN_GENERATING_PROCESS'))
                }
              }
            }
          })
        }

        // Compare action
        if (
          taskData.workflowType === WorkflowType.CHANGE &&
          taskData.workflowStepType &&
          [WorkflowStepType.REVISE_EDIT, WorkflowStepType.REVIEW, WorkflowStepType.APPROVE].includes(
            taskData.workflowStepType as WorkflowStepType
          ) &&
          taskData.files?.[0]?.draftFsi?.id
        ) {
          subViewMenu.push({
            value: 'ACTION_COMMAND_COMPARE',
            action: () => {
              const fileId = taskData.files?.[0]?.draftFsi?.id
              if (fileId) {
                showModal(<FileDiffModal fileId={fileId} onClose={() => {}} />)
              }
            }
          })
        }

        // View Comments
        if (taskData.flags?.canViewComments && taskData.workflowType !== WorkflowType.QC_REVIEW && workflowData?.id) {
          subViewMenu.push({
            value: 'VIEW_COMMENTS_ACTION',
            action: () => {
              showModal(<WorkflowCommentsModal workflow={workflowData} />)
            }
          })
        }

        if (subViewMenu.length > 0) {
          lowerMenu.push({ value: 'ACTION_COMMAND_VIEW', submenu: subViewMenu })
        }

        // Properties action
        if (
          !taskData.flags?.multifileWorkflow &&
          taskData.flags?.canChangeProperties &&
          taskData.files &&
          taskData.files.length > 0
        ) {
          lowerMenu.push({
            value: 'BROWSER_ACTION_PROPERTIES',
            action: () => {
              const fileId = taskData.files?.[0]?.draftFsi?.id
              if (fileId) {
                showModal(
                  <FsiPropertiesModal
                    fileId={fileId}
                    onPropertiesSaved={() => {
                      toast.success(t('PROPERTIES_UPDATED_SUCCESS_MSG'))
                    }}
                    onClose={() => {}}
                    isModal={true}
                  />
                )
              }
            }
          })
        }

        // Versions action
        if (!taskData.flags?.multifileWorkflow && taskData.files?.[0]) {
          const excludedSteps = [
            WorkflowStepType.ACKNOWLEDGE_EOS,
            WorkflowStepType.DOWNLOAD_EOS,
            WorkflowStepType.REVIEW_QC,
            WorkflowStepType.APPROVE_QC
          ]

          // Skip if workflow step is excluded
          if (taskData.workflowStepType && excludedSteps.includes(taskData.workflowStepType as WorkflowStepType)) {
            // Do nothing - excluded step type
          } else {
            // Determine which file to use based on type and ACL level
            const file = (() => {
              if (
                taskData.files[0].draftFsi &&
                taskData.files[0].draftFsi.type !== FsiType.PLACEHOLDER &&
                !([AclLevel.NONE, AclLevel.BROWSE] as string[]).includes((taskData.files[0].draftFsi as any).aclLevel)
              ) {
                return taskData.files[0].draftFsi
              } else if (
                taskData.files[0].sourceFsi &&
                taskData.files[0].sourceFsi.type !== FsiType.PLACEHOLDER &&
                !([AclLevel.NONE, AclLevel.BROWSE] as string[]).includes((taskData.files[0].sourceFsi as any).aclLevel)
              ) {
                return taskData.files[0].sourceFsi
              }
              return null
            })()

            if (file) {
              lowerMenu.push({
                value: 'BROWSER_ACTION_VERSIONS',
                action: () => {
                  showModal(<VersionsModal fsiId={String(file.id)} />)
                }
              })
            }
          }
        }

        // Manage Documents (multi-file workflows)
        if (taskData.flags?.multifileWorkflow) {
          lowerMenu.push({
            value: 'ACTION_COMMAND_COMPARE_MULTI',
            action: () => {
              showModal(<TaskManageDocsModal task={taskData} />)
            }
          })
        }

        // Download Document
        if (taskData.flags?.canDownloadFile) {
          lowerMenu.push({
            value: 'ACTION_COMMAND_DOWNLOAD_DOCUMENT',
            action: async () => {
              const draftFsi = taskData.files?.[0]?.draftFsi
              if (!draftFsi) {
                toast.info(t('NO_FILES_TO_BE_DOWNLOADED'))
                return
              }

              try {
                toast.info(t('BROWSER_DOWNLOAD_IS_PREPARING'))
                const downloadInfo = await getFileDownloadUrl(draftFsi.id)
                if (downloadInfo?.message) {
                  toast.info(downloadInfo.message)
                }
                if (downloadInfo?.url) {
                  window.open(downloadInfo.url, '_blank')
                } else {
                  toast.error(t('DOWNLOAD_FAILED'))
                }
              } catch (error) {
                console.error('Download error:', error)
                toast.error(t('DOWNLOAD_FAILED'))
              }
            }
          })
        }

        // Upload Document submenu
        if (taskData.flags?.canUploadFile) {
          const subMenuUpload: MenuItemData[] = []

          if (taskData.flags?.canUploadFromLocalPC) {
            subMenuUpload.push({
              value: 'FROM_MY_COMPUTER',
              action: () => {
                if (typeof taskData.workflowDraftFolderId === 'number') {
                  showModal(
                    <UploadFileRequestModal
                      parentId={taskData.workflowDraftFolderId}
                      params={{
                        workflowId: workflowData?.id,
                        task: taskData
                      }}
                      onSuccess={() => {}}
                      onFailure={() => {}}
                    />
                  )
                } else {
                  toast.error(t('NO_FOLDER_SPECIFIED'))
                }
              }
            })

            subMenuUpload.push({
              value: 'FROM_RCM',
              action: () => {
                setCurrentTaskForUpload(taskData)
                setIsFsiPickerOpen(true)
              }
            })
          }

          if (taskData.flags?.canUploadFromGDrive) {
            subMenuUpload.push({
              value: 'FROM_GOOGLE_DRIVE',
              action: async () => {
                const fileId = taskData.files?.[0]?.draftFsi?.id
                if (!fileId) {
                  toast.error(t('NO_FILE_TO_UPLOAD'))
                  return
                }
                try {
                  await api.files.uploadFromGoogleDrive(fileId)
                  toast.success(t('DOC_UPLOADED_MSG'))
                } catch (error: any) {
                  toast.error(error.message || t('UPLOAD_FAILED'))
                }
              }
            })
          }

          if (taskData.flags?.canUploadFromOneDrive) {
            subMenuUpload.push({
              value: 'DOWNLOAD_FROM_OFFICE_365',
              action: async () => {
                const fileId = taskData.files?.[0]?.draftFsi?.id
                if (!fileId) {
                  toast.error(t('NO_FILE_TO_UPLOAD'))
                  return
                }
                try {
                  await api.files.uploadFromOneDrive(fileId)
                  toast.success(t('BROWSER_UPLOAD_FILE_O365_SUCCESS_MSG'))
                } catch (error: any) {
                  toast.error(error.message || t('UPLOAD_FAILED'))
                }
              }
            })
          }

          if (subMenuUpload.length > 0) {
            lowerMenu.push({ value: 'ACTION_COMMAND_UPLOAD_DOCUMENT', submenu: subMenuUpload })
          }

          // Edit in Google Docs/Sheets
          const isExistingDoc = taskData.files?.[0]?.draftFsi
          if (isExistingDoc && taskData.flags?.canUploadFromGDrive) {
            const fileExtension = taskData.files?.[0]?.draftFsi?.name?.split('.').pop()?.toLowerCase()
            let editAction = null

            if (['doc', 'docx', 'txt'].includes(fileExtension || '')) {
              editAction = 'EDIT_IN_GOOGLE_DOCS_ACTION'
            } else if (['csv', 'xls', 'xlsx'].includes(fileExtension || '')) {
              editAction = 'EDIT_IN_GOOGLE_SHEETS_ACTION'
            }

            if (editAction) {
              lowerMenu.push({
                value: editAction,
                action: async () => {
                  const fileId = taskData.files?.[0]?.draftFsi?.id
                  if (!fileId) {
                    toast.error(t('NO_FILE_TO_EDIT'))
                    return
                  }
                  try {
                    await api.files.editInGoogleDocs(fileId)
                  } catch (error: any) {
                    toast.error(error.message || t('EDIT_FAILED'))
                  }
                }
              })
            }
          }

          // Edit in Office 365
          if (isExistingDoc && taskData.flags?.canUploadFromOneDrive) {
            lowerMenu.push({
              value: 'EDIT_IN_OFFICE_365_ACTION',
              action: async () => {
                const fileId = taskData.files?.[0]?.draftFsi?.id
                const fileExtension = taskData.files?.[0]?.draftFsi?.name?.split('.').pop()?.toLowerCase()
                if (!fileId) {
                  toast.error(t('NO_FILE_TO_EDIT'))
                  return
                }
                try {
                  await api.files.editInOffice365(fileId, fileExtension)
                } catch (error: any) {
                  if (error.message === 'EDIT_IN_OFFICE365_DOC_NOT_SUPPORTED') {
                    toast.error(t('EDIT_IN_OFFICE365_DOC_NOT_SUPPORTED'))
                  } else {
                    toast.error(error.message || t('EDIT_FAILED'))
                  }
                }
              }
            })
          }
        }

        // Add divider and lower menu items
        if (lowerMenu.length > 0) {
          if (menu.length > 0) {
            menu.push({ divider: true })
          }
          menu.push(...lowerMenu)
        }
      }
    }

    // MANAGE CR submenu (for all task types)
    const manageCrSubmenu: MenuItemData[] = []

    if (taskData.canEditWorkflow && workflowData?.id) {
      manageCrSubmenu.push({
        value: 'EDIT',
        action: () => {
          showModal(<WorkflowEditModal workflowId={workflowData.id} workflow={workflowData} />)
        }
      })
    }

    if (taskData.canAddUserToNOfAllTaskGroup && workflowData?.id) {
      manageCrSubmenu.push({
        value: 'CR_ACTION_ADD_COLLABORATORS_TO_CURRENT_PHASE',
        action: () => {
          showModal(<WorkflowCollaboratorsModal workflowId={workflowData.id} workflow={workflowData} />)
        }
      })
    }

    if (taskData.canEditWorkflowReviewers && workflowData?.id) {
      manageCrSubmenu.push({
        value: 'BROWSER_ACTION_REVIEWERS',
        action: () => {
          showModal(<WorkflowReviewModal workflowId={workflowData.id} workflow={workflowData} />)
        }
      })
    }

    if (taskData.canEditWorkflowApprovers && workflowData?.id) {
      const isQcReview = workflowData?.workflowType === 'QC_REVIEW'
      manageCrSubmenu.push({
        value: isQcReview ? 'WF_PHASE_QC_APPROVAL' : 'BROWSER_ACTION_APPROVERS',
        action: () => {
          if (isQcReview) {
            showModal(<WorkflowQcApproversModal workflowId={workflowData.id} workflow={workflowData} />)
          } else {
            showModal(<WorkflowApproversModal workflowId={workflowData.id} workflow={workflowData} />)
          }
        }
      })
    }

    if (taskData.canEditWorkflowTrainees && workflowData?.id) {
      manageCrSubmenu.push({
        value: 'BROWSER_ACTION_TRAINEES',
        action: () => {
          showModal(<WorkflowTraineesModal workflowId={workflowData.id} workflow={workflowData} />, {
            keepParentModalsOpen: true
          })
        }
      })
    }

    if (taskData.canEditRejectionReviewers && workflowData?.id) {
      manageCrSubmenu.push({
        value: 'BROWSER_ACTION_REJECTION_REVIEWERS',
        action: () => {
          showModal(<WorkflowRejectionReviewersModal workflowId={workflowData.id} workflow={workflowData} />, {
            keepParentModalsOpen: true
          })
        }
      })
    }

    if (manageCrSubmenu.length > 0) {
      menu.push({ value: 'BROWSER_ACTION_MANAGE_CR', submenu: manageCrSubmenu })
    }

    return menu
  }

  useEffect(() => {
    if (isOpen && !optionsLoaded) {
      // Load full task details to get all flags and permissions
      api.tasks
        .getTask(String(task.id))
        .then((data) => {
          const fullTask = data as Task
          if (fullTask?.workflowId) {
            api.workflows
              .getWorkflow(fullTask?.workflowId)
              .then((workflowData) => {
                setItems(buildMenuOptions(fullTask, workflowData))
                setOptionsLoaded(true)
              })
              .catch((workflowError) => {
                console.error('Failed to load workflow details:', workflowError)
                setItems(buildMenuOptions(fullTask))
                setOptionsLoaded(true)
              })
          } else {
            setItems(buildMenuOptions(fullTask))
            setOptionsLoaded(true)
          }
        })
        .catch((error) => {
          console.error('Failed to load task details:', error)
          setItems([
            {
              value: 'ERROR_LOADING_OPTIONS',
              disabled: true
            }
          ])
        })
    }
  }, [isOpen, optionsLoaded, task.id])

  const handleDocumentSelect = async (fsi: any) => {
    if (!fsi || !fsi.id || !currentTaskForUpload) {
      setIsFsiPickerOpen(false)
      setCurrentTaskForUpload(null)
      return
    }

    try {
      const folderId = currentTaskForUpload.workflowDraftFolderId
      if (!folderId) {
        toast.error(t('NO_FOLDER_SPECIFIED'))
        return
      }

      // Call API to upload version from existing document
      await api.files.uploadVersionFromExistingDocument(fsi.id, folderId)
      toast.success(t('BROWSER_UPLOAD_FILE_SUCCESS_MSG'))
    } catch (error: any) {
      console.error('Upload from RCM failed:', error)
      toast.error(error.message || t('UPLOAD_FAILED'))
    } finally {
      setIsFsiPickerOpen(false)
      setCurrentTaskForUpload(null)
    }
  }

  return (
    <>
      <ActionsMenu
        menuId={`action-menu-tasks-${task.id}`}
        onToggle={(open) => {
          setIsOpen(open)
          if (!open) {
            // Reset when menu closes to reload fresh data next time
            setOptionsLoaded(false)
          }
        }}
        items={items}
      />
      {isFsiPickerOpen && (
        <div style={{ textAlign: 'left' }}>
          <FsiPickerModal
            options={{
              selector: 'file',
              showRC: true,
              showSystem: false
            }}
            title={t('ACTION_COMMAND_UPLOAD_DOCUMENT')}
            isOpen={isFsiPickerOpen}
            onClose={() => {
              setIsFsiPickerOpen(false)
              setCurrentTaskForUpload(null)
            }}
            onSelect={handleDocumentSelect}
          />
        </div>
      )}
    </>
  )
}

export default TaskMenu
